import React, { Fragment } from 'react';
import { Fade, PopperPlacementType } from '@mui/material';

import {
  NotificationsButtonStyled,
  NotificationsPopper,
  NotificationsPaper,
  NotificationsContainer
} from './notifications-button.style';
import { NotificationsSections } from './notifications-sections.component';
import { NotificationsList } from './notifications-list.component';
import { NotificationsButtonContext } from './notifications-button.context';

function NotificationsButton({
  children,
  totalErrors,
  isSuggestion = false,
  defaultOpen = true
}: {
  children: React.ReactNode;
  totalErrors: number;
  isSuggestion?: boolean;
  defaultOpen?: boolean;
}): JSX.Element {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(buttonRef.current);
  const [open, setOpen] = React.useState(defaultOpen);
  const [placement] = React.useState<PopperPlacementType>('bottom-end');

  const errorsLabel = React.useMemo(() => {
    if (isSuggestion) {
      return totalErrors === 1
        ? `There is ${totalErrors} suggestion`
        : `There are ${totalErrors} suggestions`;
    }
    return totalErrors === 1 ? `There is ${totalErrors} error` : `There are ${totalErrors} errors`;
  }, [isSuggestion, totalErrors]);

  const handleClick = (): void => setOpen((prev: boolean) => !prev);

  React.useEffect(() => setAnchorEl(buttonRef.current), [totalErrors]);

  if (totalErrors === 0) return <Fragment></Fragment>;

  return (
    <NotificationsButtonContext.Provider
      value={{
        showNotifications: open,
        setShowNotifications: (value: boolean): void => setOpen(value),
        isSuggestion,
        defaultOpen
      }}
    >
      <NotificationsPopper open={open} anchorEl={anchorEl} placement={placement} transition>
        {({ TransitionProps }): JSX.Element => (
          <Fade {...TransitionProps} timeout={350}>
            <NotificationsPaper>
              <NotificationsContainer>{children}</NotificationsContainer>
            </NotificationsPaper>
          </Fade>
        )}
      </NotificationsPopper>

      <NotificationsButtonStyled
        onClick={handleClick}
        ref={buttonRef}
        aria-haspopup='true'
        color={isSuggestion ? 'info' : 'error'}
      >
        <div>
          <span>{errorsLabel}</span>
        </div>
      </NotificationsButtonStyled>
    </NotificationsButtonContext.Provider>
  );
}

NotificationsButton.Sections = NotificationsSections;
NotificationsButton.List = NotificationsList;

export { NotificationsButton };
